import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "./utils";

export const Table = ({ columns = [], data = [], emptyText = "No records found.", getRowKey, className }) => (
  <div className={cn("overflow-hidden rounded-lg border border-slate-200 bg-white", className)}>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            {columns.map((column) => (
              <th key={column.key || column.header} className="px-4 py-3">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.length ? data.map((row, rowIndex) => (
            <tr key={getRowKey?.(row) || row.primaryId || row.id || rowIndex} className="hover:bg-slate-50">
              {columns.map((column) => (
                <td key={column.key || column.header} className="px-4 py-3 text-slate-700">
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td className="px-4 py-8 text-center text-slate-500" colSpan={columns.length || 1}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export const Tabs = ({ className, onChange, tabs = [], value }) => (
  <div className={cn("flex gap-1 rounded-md bg-slate-100 p-1", className)} role="tablist">
    {tabs.map((tab) => {
      const active = value === tab.value;
      return (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange?.(tab.value)}
          className={cn(
            "min-h-9 rounded px-3 text-sm font-semibold transition",
            active ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
          )}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

export const AccordionSummary = ({ children, className, expandIcon, ...props }) => (
  <summary
    className={cn("flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-slate-950 [&::-webkit-details-marker]:hidden", className)}
    {...props}
  >
    <span className="min-w-0 flex-1">{children}</span>
    <span className="shrink-0 text-slate-500">{expandIcon || <ChevronDown className="h-4 w-4" aria-hidden="true" />}</span>
  </summary>
);

export const AccordionDetails = ({ children, className }) => (
  <div className={cn("px-4 pb-4 text-sm text-slate-700", className)}>
    {children}
  </div>
);

export const Accordion = ({ className, defaultExpanded, defaultOpen = false, children, title }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!title) {
    return (
      <details open={defaultExpanded || defaultOpen} className={cn("rounded-md border border-slate-200 bg-white", className)}>
        {children}
      </details>
    );
  }

  return (
    <div className={cn("rounded-md border border-slate-200 bg-white", className)}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-slate-950"
        onClick={() => setOpen((value) => !value)}
      >
        <span>{title}</span>
        <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && <div className="border-t border-slate-200 px-4 py-3 text-sm text-slate-700">{children}</div>}
    </div>
  );
};
